import type { LegalDocument } from '@/schemas/schema';
import type { OrgContractDocumentItem } from '@/modules/org/contracts/types/contract-document.types';
import { loadTemplateReview } from './TemplateReviewStorage';

export interface ReviewData {
  document: LegalDocument;
  formData: Record<string, unknown>;
  templateId: string;
  templateSlug: string;
  templateTitle: string;
  storedAt: string;
}

/** A document is reviewable only once it has a content tree to render. */
const isRenderable = (document: LegalDocument | null | undefined): document is LegalDocument =>
  !!document && Array.isArray(document.content);

/**
 * Read the review payload out of a contract record.
 *
 * Returns null when the record carries no usable document — a row can
 * legitimately exist before its `Document` is written, because the generate
 * flow creates it up front and fills it in once generation completes.
 */
export function parseContractReviewData(
  contract: OrgContractDocumentItem | null | undefined
): ReviewData | null {
  if (!contract?.Document) return null;

  let document: LegalDocument;
  try {
    document = JSON.parse(contract.Document) as LegalDocument;
  } catch (err) {
    console.error('Failed to parse server document:', err);
    return null;
  }
  if (!isRenderable(document)) return null;

  // Form data only feeds signatory extraction, so a bad blob shouldn't block
  // reviewing the document itself.
  let formData: Record<string, unknown> = {};
  try {
    const parsed: unknown = contract.FormData ? JSON.parse(contract.FormData) : {};
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      formData = parsed as Record<string, unknown>;
    }
  } catch (err) {
    console.error('Failed to parse server form data:', err);
  }

  return {
    document,
    formData,
    templateId: contract.TemplateId ?? '',
    templateSlug: contract.TemplateSlug,
    templateTitle: contract.TemplateTitle,
    storedAt: contract.CreatedDate ?? new Date().toISOString(),
  };
}

/**
 * Resolve what to review, given the URL and whatever the server returned.
 *
 * The server record is authoritative whenever the URL names one. The local
 * snapshot is keyed by template *slug*, not document id, so every contract
 * generated from the same template shares a single entry — falling back to it
 * for a known document could render a different contract's content. Showing
 * "nothing to review" is strictly better than showing the wrong document.
 *
 * The snapshot is therefore only consulted when there is no server record to
 * speak of: legacy and guest flows that never persisted one.
 */
export function resolveReviewData({
  contract,
  documentId,
  slug,
}: {
  contract: OrgContractDocumentItem | null | undefined;
  documentId: string;
  slug: string;
}): ReviewData | null {
  const fromServer = parseContractReviewData(contract);
  if (fromServer) return fromServer;

  if (documentId) return null;

  const stored = loadTemplateReview(slug);
  if (!stored || !isRenderable(stored.document)) return null;
  return { ...stored, storedAt: stored.storedAt || new Date().toISOString() };
}
